/* Created by Interactive 63 - https://interactive63.com */
/**
 * The timeline's label column: one row per animated or selected element, with its tracks
 * beneath. Rows carry the expand toggle, summary and per-track keyframe diamonds, previous and
 * next keyframe arrows, and the mute and remove buttons.
 */
import { adjacentKeyframeTime, findTrack, keyframesAt } from '../../../core/timeline/keyframes';
import { PROPERTIES } from '../../../core/properties/registry';
import { selectElements } from '../../store/actions';
import { keyAllAtPlayhead, toggleKeyframeAtPlayhead } from '../../store/property-actions';
import {
  removeElementAnimation,
  removeTracks,
  selectAllKeyframesOn,
  setTrackPlayback,
  setTracksEnabled,
} from '../../store/timeline-actions';
import { h } from '../../ui/dom';
import { icon } from '../../ui/icons';
import { jsBadge, MORPH_JS_TITLE } from '../../ui/js-badge';
import { showContextMenu, type MenuItem } from './context-menu';
import { setTrackOrient } from '../../store/motion-actions';
import { openPlaybackPopover } from './playback-popover';
import type { TimelineHost } from './host';
import type { TimelineRow } from './layout';
import type { Track } from '../../../core/model/types';

export class LabelColumn {
  readonly el: HTMLElement;
  private rowKeys = '';
  private rows: TimelineRow[] = [];

  /** Builds the column. `onToggleCollapse` flips an element's collapsed state. */
  constructor(
    private readonly host: TimelineHost,
    private readonly collapsed: ReadonlySet<string>,
    private readonly onToggleCollapse: (elementId: string) => void,
  ) {
    this.el = h('div', { class: 'tl-labels', role: 'tree', 'aria-label': 'Animated elements' });
  }

  /** Renders the rows. Does nothing structural if the same rows are already shown. */
  render(rows: TimelineRow[]): void {
    this.rows = rows;
    const key = rows
      .map((r) => `${r.key}:${this.collapsed.has(r.elementId) ? 'c' : 'o'}`)
      .join('|');
    if (key === this.rowKeys) {
      this.refreshStates();
      return;
    }
    this.rowKeys = key;
    this.el.replaceChildren(...rows.map((row) => this.buildRow(row)));
    this.refreshStates();
  }

  /** Builds one label row. */
  private buildRow(row: TimelineRow): HTMLElement {
    const { store } = this.host;
    const { project } = store.getState();
    const meta = project.elements[row.elementId];

    if (row.kind === 'element') {
      const isCollapsed = this.collapsed.has(row.elementId);
      const toggle = h(
        'button',
        {
          type: 'button',
          class: 'tl-toggle',
          'aria-label': isCollapsed ? 'Expand' : 'Collapse',
          'aria-expanded': String(!isCollapsed),
          title: isCollapsed ? 'Expand' : 'Collapse',
        },
        icon('chevron'),
      );
      toggle.classList.toggle('is-open', !isCollapsed);
      toggle.addEventListener('click', () => {
        this.onToggleCollapse(row.elementId);
      });
      const label = h(
        'button',
        { type: 'button', class: 'tl-label', title: meta?.label ?? row.elementId },
        meta?.label ?? row.elementId,
      );
      label.addEventListener('click', (event) => {
        selectElements(
          store,
          [row.elementId],
          event.shiftKey || event.ctrlKey || event.metaKey ? 'toggle' : 'replace',
        );
      });
      label.addEventListener('contextmenu', (event) => {
        event.preventDefault();
        showContextMenu(event.clientX, event.clientY, this.elementMenu(row.elementId));
      });
      const diamond = h(
        'button',
        {
          type: 'button',
          class: 'keyframe-toggle tl-summary',
          'aria-label': 'Add keyframes for all tracks at the playhead',
          title: 'Add keyframes for all tracks (W)',
        },
        icon('stop'),
      );
      diamond.addEventListener('click', () => {
        keyAllAtPlayhead(store, [row.elementId], this.host.playback.currentTime);
      });
      const mute = h(
        'button',
        {
          type: 'button',
          class: 'tl-mute',
          'aria-label': 'Mute all tracks',
          title: 'Mute all tracks',
        },
        '◐',
      );
      mute.addEventListener('click', () => {
        this.toggleMute(row.elementId);
      });
      const remove = h(
        'button',
        {
          type: 'button',
          class: 'tl-remove',
          'aria-label': 'Remove all animation',
          title: 'Remove all animation',
        },
        '×',
      );
      remove.addEventListener('click', () => {
        removeElementAnimation(store, [row.elementId]);
      });
      return h(
        'div',
        {
          class: 'tl-row tl-element-row',
          role: 'treeitem',
          'aria-expanded': String(!isCollapsed),
          'data-row': row.key,
          style: { height: `${row.height}px` },
        },
        toggle,
        label,
        h('span', { class: 'tl-actions' }, mute, remove),
        diamond,
      );
    }

    const property = row.track?.property;
    const definition = property ? PROPERTIES[property] : undefined;
    const diamond = h(
      'button',
      {
        type: 'button',
        class: 'keyframe-toggle',
        'aria-label': `Toggle ${definition?.label ?? ''} keyframe at the playhead`,
      },
      icon('stop'),
    );
    diamond.addEventListener('click', () => {
      if (!property) return;
      toggleKeyframeAtPlayhead(
        store,
        [row.elementId],
        property,
        this.host.playback.currentTime,
        this.host.inspector.readValue,
      );
    });
    const label = h(
      'span',
      { class: 'tl-label tl-track-label' },
      definition?.label ?? '',
      definition && !definition.cssOnly ? jsBadge(MORPH_JS_TITLE) : null,
    );
    const badges = h('span', { class: 'tl-badges' });
    label.addEventListener('contextmenu', (event) => {
      event.preventDefault();
      if (row.track)
        showContextMenu(
          event.clientX,
          event.clientY,
          this.trackMenu(row.track.id, row.track.enabled, event),
        );
    });
    const previous = h(
      'button',
      {
        type: 'button',
        class: 'key-nav',
        'aria-label': `Previous ${definition?.label ?? ''} keyframe`,
        title: 'Previous keyframe',
      },
      '‹',
    );
    const next = h(
      'button',
      {
        type: 'button',
        class: 'key-nav',
        'aria-label': `Next ${definition?.label ?? ''} keyframe`,
        title: 'Next keyframe',
      },
      '›',
    );
    for (const [button, direction] of [
      [previous, 'previous'],
      [next, 'next'],
    ] as const) {
      button.addEventListener('click', () => {
        if (!property) return;
        const target = adjacentKeyframeTime(
          store.getState().project,
          [row.elementId],
          this.host.playback.currentTime,
          direction,
          property,
        );
        if (target !== undefined) this.host.playback.seek(target);
      });
    }
    return h(
      'div',
      {
        class: 'tl-row tl-track-row',
        role: 'treeitem',
        'data-row': row.key,
        style: { height: `${row.height}px` },
      },
      label,
      badges,
      h('span', { class: 'tl-nav' }, previous, next),
      diamond,
    );
  }

  /** Updates diamonds, arrows, mute state and selection highlight without rebuilding rows. */
  refreshStates(): void {
    const { project, editor } = this.host.store.getState();
    const time = this.host.playback.currentTime;
    for (const row of this.rows) {
      const el = this.el.querySelector<HTMLElement>(`[data-row="${CSS.escape(row.key)}"]`);
      if (!el) continue;
      const diamond = el.querySelector<HTMLElement>('.keyframe-toggle');
      if (row.kind === 'element') {
        const tracks = project.tracks.filter((t) => t.elementId === row.elementId);
        const keyed = keyframesAt(project, row.elementId, time).length > 0;
        diamond?.classList.toggle('is-key', keyed);
        diamond?.classList.toggle('is-track', !keyed && tracks.length > 0);
        diamond?.classList.toggle('is-none', tracks.length === 0);
        el.classList.toggle('is-selected', editor.selection.elementIds.includes(row.elementId));
        el.querySelector('.tl-mute')?.classList.toggle(
          'is-on',
          tracks.length > 0 && tracks.every((t) => !t.enabled),
        );
        continue;
      }
      const property = row.track?.property;
      if (!property) continue;
      const track = findTrack(project, row.elementId, property);
      this.refreshBadges(el, track);
      const keyed =
        !!track &&
        keyframesAt(project, row.elementId, time).some((kf) => track.keyframes.includes(kf));
      diamond?.classList.toggle('is-key', keyed);
      diamond?.classList.toggle('is-track', !keyed);
      el.classList.toggle('is-disabled', track ? !track.enabled : false);
      const [previous, next] = Array.from(el.querySelectorAll<HTMLButtonElement>('.key-nav'));
      if (previous)
        previous.disabled =
          adjacentKeyframeTime(project, [row.elementId], time, 'previous', property) === undefined;
      if (next)
        next.disabled =
          adjacentKeyframeTime(project, [row.elementId], time, 'next', property) === undefined;
    }
  }

  /** Shows the repeat and play-once indicators on a track row. */
  private refreshBadges(row: HTMLElement, track: Track | undefined): void {
    const holder = row.querySelector('.tl-badges');
    if (!holder) return;
    const badges: HTMLElement[] = [];
    if (track?.repeat) {
      const times = track.repeat.count === 'infinite' ? '∞' : `×${track.repeat.count}`;
      badges.push(
        h(
          'span',
          {
            class: 'tl-badge',
            title: `Repeats ${times === '∞' ? 'for the whole scene' : `${track.repeat.count} times`}${track.repeat.alternate ? ', forwards then backwards' : ''}`,
          },
          `↻ ${times}`,
        ),
      );
    }
    if (track?.restartWithScene === false) {
      badges.push(
        h(
          'span',
          {
            class: 'tl-badge',
            title: "Plays once, then holds. Doesn't restart when the scene loops.",
          },
          '1×',
        ),
      );
    }
    const key = badges.map((b) => b.textContent).join('|');
    if (holder.getAttribute('data-key') === key) return;
    holder.setAttribute('data-key', key);
    holder.replaceChildren(...badges);
  }

  /** Disables all of an element's tracks, or enables them all if they are already disabled. */
  private toggleMute(elementId: string): void {
    const tracks = this.host.store
      .getState()
      .project.tracks.filter((t) => t.elementId === elementId);
    const anyEnabled = tracks.some((t) => t.enabled);
    setTracksEnabled(
      this.host.store,
      tracks.map((t) => t.id),
      !anyEnabled,
    );
  }

  /** Context menu for an element row. */
  private elementMenu(elementId: string): MenuItem[] {
    const collapsed = this.collapsed.has(elementId);
    return [
      {
        label: 'Select element',
        onSelect: () => {
          selectElements(this.host.store, [elementId]);
        },
      },
      {
        label: 'Remove all animation',
        onSelect: () => {
          removeElementAnimation(this.host.store, [elementId]);
        },
      },
      {
        label: 'Offset animation…',
        disabled: this.host.store.getState().editor.selection.elementIds.length < 2,
        onSelect: () => {
          this.host.offset.open(window.innerWidth / 2 - 120, window.innerHeight / 2 - 100);
        },
      },
      {
        label: collapsed ? 'Expand' : 'Collapse',
        onSelect: () => {
          this.onToggleCollapse(elementId);
        },
      },
    ];
  }

  /** Context menu for a track row. */
  private trackMenu(trackId: string, enabled: boolean, event: MouseEvent): MenuItem[] {
    const track = this.host.store.getState().project.tracks.find((t) => t.id === trackId);
    const once = track?.restartWithScene === false;
    const tick = (on: boolean): string => (on ? '✓ ' : '');
    return [
      {
        label: 'Playback',
        items: [
          {
            label: `${tick(!once)}Loops with the scene`,
            onSelect: () => {
              setTrackPlayback(this.host.store, [trackId], { restartWithScene: true });
            },
          },
          {
            label: `${tick(once)}Plays once, then holds`,
            onSelect: () => {
              setTrackPlayback(this.host.store, [trackId], { restartWithScene: false });
            },
          },
          {
            label: `${tick(track?.repeat !== undefined)}Repeat…`,
            onSelect: () => {
              openPlaybackPopover(this.host.store, [trackId], {
                x: event.clientX,
                y: event.clientY,
              });
            },
          },
        ],
      },
      ...(track?.property === 'translate'
        ? [
            {
              label: `${tick(track.orient === true)}Orient along path`,
              onSelect: () => {
                setTrackOrient(this.host.store, trackId, track.orient !== true);
              },
            },
          ]
        : []),
      { separator: true },
      {
        label: enabled ? 'Disable track' : 'Enable track',
        onSelect: () => {
          setTracksEnabled(this.host.store, [trackId], !enabled);
        },
      },
      {
        label: 'Remove track',
        onSelect: () => {
          removeTracks(this.host.store, [trackId]);
        },
      },
      {
        label: 'Select all keyframes on this track',
        onSelect: () => {
          selectAllKeyframesOn(this.host.store, [trackId]);
        },
      },
    ];
  }
}
